import { useForm } from '@tanstack/react-form';
import { Clock, Stethoscope } from 'lucide-react';
import { z } from 'zod';

import { type GetAllReservations } from '@virtual-care/client';
import { formatReservationDateTime } from '@virtual-care/isomorphic';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import { FormMessage, errorToFormIssue, useAction } from '@virtual-care/ui';

import { DoctorSelect } from '../../components/doctor-select.tsx';
import { SpecialitySelect } from '../../components/speciality-select.tsx';
import { PatientInfo } from './patient-info.tsx';

type ReservationRecord = GetAllReservations['records'][number];

interface ReassignDoctorDialogProps {
  selectedReservation: ReservationRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const schema = z.object({
  specialityId: z.string().min(1, 'Please select a speciality'),
  doctorId: z.string().min(1, 'Please select a doctor'),
});

export function ReassignDoctorDialog({
  selectedReservation,
  open,
  onOpenChange,
  onSuccess,
}: ReassignDoctorDialogProps) {
  const assignDoctorMutation = useAction(
    'PATCH /reservations/{reservationId}/assign-doctor',
    {
      invalidate: ['GET /reservations'],
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Failed to reassign doctor:', error);
      },
    },
  );

  const form = useForm({
    defaultValues: {
      specialityId: selectedReservation.specialityId,
      doctorId: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          await assignDoctorMutation.mutateAsync({
            reservationId: selectedReservation.id,
            doctorId: props.value.doctorId,
            specialityId: props.value.specialityId,
          });
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign New Doctor</DialogTitle>
          <DialogDescription>
            This case was referred by the previous doctor. Please select a new
            speciality and doctor.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <PatientInfo patient={selectedReservation.user} />

          <div className="bg-muted/50 space-y-3 rounded-lg p-4">
            <h3 className="flex items-center gap-2 font-medium">
              <Stethoscope className="h-4 w-4" />
              Case Information
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Current Speciality:</span>
              <span className="text-sm">
                {selectedReservation.speciality.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="text-muted-foreground h-4 w-4" />
              <span className="text-sm font-medium">
                Patient's Selected Time:
              </span>
              <span className="font-mono text-sm">
                {formatReservationDateTime(selectedReservation)}
              </span>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="specialityId"
              children={(field) => <SpecialitySelect field={field} />}
            />

            <form.Subscribe
              selector={(state) => state.values.specialityId}
              children={(specialityId) => (
                <form.Field
                  name="doctorId"
                  children={(field) => (
                    <DoctorSelect
                      field={field}
                      specialityId={specialityId}
                      label="Select New Doctor"
                    />
                  )}
                />
              )}
            />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? 'Assigning...' : 'Assign Doctor'}
                  </Button>
                </DialogFooter>
              )}
            />

            <FormMessage errors={form.state.errors} />
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
